import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, ModalBody, ModalFooter } from 'reactstrap';

import globalTranslations from '../../../i18n/globalTranslations';
import { modelOf } from '../../../prop-types';
import ConfigStore from '../../../store/ConfigStore';
import { createInlineErrorModel } from '../../../util/error';
import ErrorHandler from '../../loader/ErrorHandler';
import Icon from '../Icon';
import RegionalSettingsDropdowns from '../RegionalSettingsDropdowns';

const RegionalSettingsPanelContent = ({
  configStore,
  currentValues,
  onChange,
  errorState,
  isSaving,
  onSave,
  onClose,
  onCancel,
  title,
  titleId,
  contentRef,
  onContentKeyDown,
  contentTabIndex,
  showSave,
  menuContainer,
  children,
}) => {
  const { hasError, error } = errorState;
  const handleCancel = onCancel || onClose;
  const resolvedTitle = title ?? (
    <FormattedMessage {...globalTranslations.regionalSettingsTitle} />
  );
  const { isRounded, isBoxed } = configStore.layout.corners;

  return (
    <div
      className={classNames('RegionalSettingsPanelContent', {
        'RegionalSettingsPanelContent--rounded': isRounded,
        'RegionalSettingsPanelContent--box': isBoxed,
      })}
    >
      <ModalBody className="RegionalSettingsPanelContent__body">
        <div
          ref={contentRef}
          className="RegionalSettingsPanelContent__content"
          tabIndex={contentTabIndex}
          onKeyDown={onContentKeyDown}
        >
          <h2 className="RegionalSettingsPanelContent__title" id={titleId}>
            {resolvedTitle}
          </h2>
          {children || (
            <RegionalSettingsDropdowns
              {...currentValues}
              onChange={onChange}
              disabled={isSaving}
              menuContainer={menuContainer}
            />
          )}
          {hasError && (
            <div className="RegionalSettingsPanelContent__error">
              <ErrorHandler error={createInlineErrorModel(error)} />
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter className="RegionalSettingsPanelContent__footer">
        <div className="RegionalSettingsPanelContent__footer-actions">
          {showSave && (
            <Button
              className={classNames(
                'RegionalSettingsPanelContent__save-button',
                'RegionalSettingsPanelContent__footer-control'
              )}
              color="primary"
              disabled={isSaving}
              onClick={onSave}
            >
              <span>
                <Icon
                  name={isSaving ? 'circle-o-notch' : 'check'}
                  spin={isSaving}
                />{' '}
                <FormattedMessage {...globalTranslations.saveTitle} />
              </span>
            </Button>
          )}
          <button
            type="button"
            className={classNames(
              'RegionalSettingsPanelContent__cancel-button',
              'RegionalSettingsPanelContent__footer-control'
            )}
            disabled={isSaving}
            onClick={handleCancel}
          >
            <FormattedMessage
              {...globalTranslations.regionalSettingsCancelAndReturnToStore}
            />
          </button>
        </div>
      </ModalFooter>
    </div>
  );
};

RegionalSettingsPanelContent.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  currentValues: PropTypes.shape({
    country: PropTypes.object,
    currency: PropTypes.object,
    language: PropTypes.object,
  }),
  onChange: PropTypes.func,
  errorState: PropTypes.shape({
    hasError: PropTypes.bool.isRequired,
    error: PropTypes.any,
  }).isRequired,
  isSaving: PropTypes.bool.isRequired,
  onSave: PropTypes.func,
  onClose: PropTypes.func,
  onCancel: PropTypes.func,
  title: PropTypes.node,
  titleId: PropTypes.string,
  contentRef: PropTypes.shape({ current: PropTypes.any }),
  onContentKeyDown: PropTypes.func,
  contentTabIndex: PropTypes.number,
  showSave: PropTypes.bool,
  menuContainer: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.func,
  ]),
  children: PropTypes.node,
};

RegionalSettingsPanelContent.defaultProps = {
  currentValues: undefined,
  onChange: undefined,
  onSave: undefined,
  onClose: undefined,
  onCancel: undefined,
  title: undefined,
  titleId: undefined,
  contentRef: undefined,
  onContentKeyDown: undefined,
  contentTabIndex: undefined,
  showSave: true,
  menuContainer: undefined,
  children: undefined,
};

export default inject('configStore')(observer(RegionalSettingsPanelContent));
